/**
 * The edit layer: typed, range-checked mutations of a parsed SAVE.DAT.
 *
 * Two rules govern everything here.
 *
 * **Only the scalar territory.** Stats, skills, perks, traits, GVARs, kill
 * counts, PC stats, hit points. No inventory. Adding an item means writing a
 * nested `Object` whose size depends on protos the save does not carry, and
 * getting that subtly wrong is how a save editor corrupts a playthrough two
 * hours later. Inventory goes through the engine's own serializer via the debug
 * bridge — see `docs/design/save-editor.md`.
 *
 * **Say what the engine will do with it.** Fallout 2 stores derived stats but
 * recomputes them only on in-game changes, never on load
 * (`critterUpdateDerivedStats` is reached from `critterSetBaseStat`,
 * `critterSetBonusStat`, the character editor and new-game setup — and from
 * nothing in `_master_load_list`). So raising Strength in a save file leaves
 * carry weight, melee damage and max HP at their old values until something
 * in-game touches a SPECIAL stat. That is not a bug we can fix by writing more
 * bytes; it is a fact the editor has to tell the player. `pendingWarnings`
 * carries it.
 */

import {
  MAX_PERK_RANK,
  SKILLS_MIN_VALUE,
  TAG_SKILL_BONUS,
  NUM_TAGGED_SKILLS,
  PC_STAT_BOUNDS,
  PC_STAT_NAMES,
  PRIMARY_STAT_COUNT,
  PRIMARY_STAT_MAX,
  PRIMARY_STAT_MIN,
  SAVEABLE_STAT_COUNT,
  SKILLS_MIN_RAW_POINTS,
  SKILL_COUNT,
  SKILL_NAMES,
  STAT_NAMES,
  TRAITS_MAX_SELECTED_COUNT,
  perkIndex,
  skillIndex,
  statIndex,
  traitIndex,
} from './constants';
import {
  characterEditorLastLevel,
  characterEditorPerksOwed,
  fixedField,
  gameDifficulty,
  setCharacterEditorLastLevel,
  setCharacterEditorPerksOwed,
  type QueueEvent,
  type SaveDat,
} from './savedat';
import type { PerkRow } from './perktable';
import { forGame, type GameConstants } from './constants-fo1';
import type { SaveGameType } from './gamedefs';
import {
  CHARACTER_NAME_SIZE,
  DESCRIPTION_SIZE,
} from './constants';
import {
  levelCap,
  maxPerksOwed,
  maxSelectablePerks,
  perkGrantAssigns,
  skillDisplayCap,
  accrueSkillPoints,
  experienceBand,
  experienceForLevel,
  levelForExperience,
  levelsPerPerk,
  perkAvailability,
  perkMaxRank,
  perkSlotsThroughLevel,
  skillPointCost,
  skillPointsForOneLevel,
  EVENT_TYPE_POISON,
  EVENT_TYPE_WITHDRAWAL,
  GAME_TIME_TICKS_PER_MINUTE,
  poisonTicksToNext,
  withdrawalDurationMinutes,
  type AddictionRule,
  type PerkAvailability,
  type PerkContext,
} from './rules';

/**
 * The constant bundle for whichever game wrote this save.
 *
 * Every table this module indexes into — perks, traits, kill types, stat bounds,
 * skill descriptions — differs between the two engines, and three of them differ
 * in *length*. Reading Fallout 2's 119-entry perk table against a Fallout 1
 * save's 64-word perk block is not a cosmetic mistake: it offers the player
 * perks that game has never had, and writes ranks past the end of the block.
 * So nothing here reaches for a module-level table any more; it goes through
 * this.
 */
const K = (save: SaveDat): GameConstants => forGame(save.game);

const CRITTER_DUDE_RADIATED = 0x02;
const CRITTER_DUDE_LEVEL_UP_AVAILABLE = 0x08;
const CRITTER_DUDE_ADDICTED = 0x10;
const OBJECT_QUEUED = 0x2000;

export class EditError extends Error {
  readonly field: string;
  readonly value: number | string;
  constructor(field: string, value: number | string, message: string) {
    super(`${field}: ${message}`);
    this.name = 'EditError';
    this.field = field;
    this.value = value;
  }
}

/** Something the engine will do — or refuse to do — as a result of an edit. */
export type EditWarning = {
  kind: 'derived-stale' | 'engine-would-refuse' | 'out-of-band' | 'consistency';
  message: string;
};

/**
 * One applied change, for an undo list or a "what did I do" panel.
 *
 * `apply` is the mutation itself, kept as a value so the same edit can be
 * applied to a *different* parse of the same slot. That is not a convenience:
 * when the shell runs an inventory edit through the engine bridge, the engine's
 * own serializer rewrites the slot, and the scalar edits then have to be applied
 * on top of the file the engine just wrote rather than on top of the file the
 * editor opened. Replaying the recorded mutations is the only way to do that
 * which cannot clobber a field the engine legitimately changed (the map-file
 * list and the save's wall-clock stamp both move across a bridge write).
 *
 * It is the *only* mutation path — `SaveEditor.change()` applies it rather than
 * duplicating it — so a recorded change and the change that was made cannot
 * drift apart.
 */
export type EditRecord = {
  what: string;
  from: number | string;
  to: number | string;
  /** Safe to replay after an engine-rules write without overriding engine truth. */
  afterEngineWrite: boolean;
  /** Re-applies exactly this change to another parse of the same slot. */
  apply: (save: SaveDat) => void;
};

/**
 * Derived base stats, and the formula the engine uses when it does recompute
 * them (engine/src/stat.cc `critterUpdateDerivedStats`).
 *
 * Two consequences worth stating plainly:
 *
 *   - These are *stored*, in `baseStats`, and nothing in the load path
 *     recalculates them. Whatever the file says is what the player gets, until
 *     an in-game SPECIAL change or the character editor fires the recompute and
 *     silently overwrites the edit.
 *   - `critterSetBaseStat` refuses to set any of them in-game
 *     ("Cannot change base value of derived stats"). Writing them directly is
 *     therefore *more* powerful than the engine's own API, and correspondingly
 *     easier to get wrong.
 *
 * `STAT_UNARMED_DAMAGE` and the damage thresholds/resistances are absent because
 * the recompute does not touch them: they are yours to keep.
 */
export const DERIVED_BASE_STATS: ReadonlyMap<string, string> = new Map([
  ['MAXIMUM_HIT_POINTS', '(base + trait) STR + 2 * (base + trait) END + 15'],
  ['MAXIMUM_ACTION_POINTS', 'AGI / 2 + 5'],
  ['ARMOR_CLASS', 'AGI'],
  ['MELEE_DAMAGE', 'max(STR - 5, 1)'],
  ['CARRY_WEIGHT', '25 * STR + 25'],
  ['SEQUENCE', '2 * PER'],
  ['HEALING_RATE', 'max(END / 3, 1)'],
  ['CRITICAL_CHANCE', 'LUK'],
  ['BETTER_CRITICALS', '0'],
  ['RADIATION_RESISTANCE', '2 * END'],
  ['POISON_RESISTANCE', '5 * END'],
]);

/**
 * `traitGetStatModifier` for the SPECIAL seven (engine/src/trait.cc). Gifted
 * lifts all seven, Bruiser adds two Strength, Small Frame adds one Agility.
 * The trait modifiers for *derived* stats are deliberately not applied here:
 * `critterUpdateDerivedStats` writes the un-modified formula into baseStats and
 * lets `critterGetStat` add the trait on read. `traitDerivedModifier` is the
 * other half, for the display path.
 */
function traitSpecialModifier(stat: number, traits: ArrayLike<number>, game: SaveGameType = 'fo2'): number {
  const names = forGame(game);
  const has = (name: string) => {
    const id = names.traitIndex(name);
    return id >= 0 && (traits[0] === id || traits[1] === id);
  };
  let modifier = has('GIFTED') ? 1 : 0;
  if (stat === statIndex('STRENGTH') && has('BRUISER')) modifier += 2;
  if (stat === statIndex('AGILITY') && has('SMALL_FRAME')) modifier += 1;
  return modifier;
}

/**
 * The rest of `traitGetStatModifier` (engine/src/trait.cc:180) — the derived
 * half, which the editor needs to *display* a derived stat as the character
 * screen shows it rather than as the file stores it.
 *
 * The three subtractive ones read the stored base back, exactly as the engine
 * does (`-= critterGetBaseStat(gDude, STAT_ARMOR_CLASS)` and friends), which is
 * what makes Kamikaze zero armour class rather than take a fixed amount off it.
 * They therefore need the base stats, not just the trait list.
 */
function traitDerivedModifier(
  stat: number,
  traits: ArrayLike<number>,
  baseStats: ArrayLike<number>,
  game: SaveGameType = 'fo2',
): number {
  const names = forGame(game);
  const has = (name: string) => {
    const id = names.traitIndex(name);
    return id >= 0 && (traits[0] === id || traits[1] === id);
  };
  const base = (name: string) => baseStats[statIndex(name)] ?? 0;
  switch (STAT_NAMES[stat]) {
    case 'MAXIMUM_ACTION_POINTS':
      return has('BRUISER') ? -2 : 0;
    case 'ARMOR_CLASS':
      return has('KAMIKAZE') ? -base('ARMOR_CLASS') : 0;
    case 'MELEE_DAMAGE':
      return has('HEAVY_HANDED') ? 4 : 0;
    case 'CARRY_WEIGHT':
      return has('SMALL_FRAME') ? -10 * base('STRENGTH') : 0;
    case 'SEQUENCE':
      return has('KAMIKAZE') ? 5 : 0;
    case 'HEALING_RATE':
      return has('FAST_METABOLISM') ? 2 : 0;
    case 'CRITICAL_CHANCE':
      return has('FINESSE') ? 10 : 0;
    case 'BETTER_CRITICALS':
      return has('HEAVY_HANDED') ? -30 : 0;
    case 'RADIATION_RESISTANCE':
      return has('FAST_METABOLISM') ? -base('RADIATION_RESISTANCE') : 0;
    case 'POISON_RESISTANCE':
      return has('FAST_METABOLISM') ? -base('POISON_RESISTANCE') : 0;
    default:
      return 0;
  }
}

/**
 * The dude-only perk adjustments `critterGetStat` applies on *read* to derived
 * stats (engine/src/stat.cc:388-512) — the ones a save file has enough
 * information to reproduce.
 *
 * Two are left out on purpose and for the same reason as always: Adrenaline
 * Rush needs the current-versus-maximum hit points relationship at the moment of
 * the read, and Mirrored Shades needs to know what is in the character's hands.
 * The GAIN_* perks are handled in `computeSpecial`, where they belong.
 */
function perkDerivedModifier(stat: number, perks: ArrayLike<number>, game: SaveGameType = 'fo2'): number {
  // Every perk named below is a Fallout 2 addition (index >= 64), so on a
  // Fallout 1 save `perkIndex` returns -1 for all of them and this is uniformly
  // zero — which is the right answer, not a fallback.
  const names = forGame(game);
  const rank = (name: string) => {
    const id = names.perkIndex(name);
    return id >= 0 ? (perks[id] ?? 0) : 0;
  };
  const name = STAT_NAMES[stat];
  if (name === 'MAXIMUM_HIT_POINTS') {
    return (
      (rank('ALCOHOL_RAISED_HIT_POINTS') ? 2 : 0)
      + (rank('ALCOHOL_RAISED_HIT_POINTS_II') ? 4 : 0)
      - (rank('ALCOHOL_LOWERED_HIT_POINTS') ? 2 : 0)
      - (rank('ALCOHOL_LOWERED_HIT_POINTS_II') ? 4 : 0)
      + (rank('AUTODOC_RAISED_HIT_POINTS') ? 2 : 0)
      + (rank('AUTODOC_RAISED_HIT_POINTS_II') ? 4 : 0)
      - (rank('AUTODOC_LOWERED_HIT_POINTS') ? 2 : 0)
      - (rank('AUTODOC_LOWERED_HIT_POINTS_II') ? 4 : 0)
    );
  }
  if (name === 'DAMAGE_RESISTANCE' || name === 'DAMAGE_RESISTANCE_EXPLOSION') {
    if (rank('DERMAL_IMPACT_ARMOR')) return 5;
    if (rank('DERMAL_IMPACT_ASSAULT_ENHANCEMENT')) return 10;
    return 0;
  }
  if (
    name === 'DAMAGE_RESISTANCE_LASER'
    || name === 'DAMAGE_RESISTANCE_FIRE'
    || name === 'DAMAGE_RESISTANCE_PLASMA'
  ) {
    if (rank('PHOENIX_ARMOR_IMPLANTS')) return 5;
    if (rank('PHOENIX_ASSAULT_ENHANCEMENT')) return 10;
    return 0;
  }
  if (name === 'RADIATION_RESISTANCE' || name === 'POISON_RESISTANCE') {
    return rank('VAULT_CITY_INOCULATIONS') ? 10 : 0;
  }
  return 0;
}

/**
 * `critterGetStat` for a *derived* stat: base, plus the trait modifier, plus the
 * stored bonus, plus the read-time perk adjustments, clamped to the stat's
 * bounds. This is the number on the character screen, as opposed to the number
 * in the file, and in engine mode it is the only one shown as a value.
 */
export type DerivedDisplayBreakdown = {
  storedBase: number;
  storedBonus: number;
  traitModifier: number;
  readTimeModifier: number;
  raw: number;
  displayed: number;
  minimum: number | null;
  maximum: number | null;
};

/** The exact additive path used by `computeDerivedDisplay`, exposed for help UI. */
export function computeDerivedDisplayBreakdown(stat: number, inputs: DerivedInputs): DerivedDisplayBreakdown {
  const game = inputs.game ?? 'fo2';
  const storedBase = inputs.baseStats[stat]!;
  const storedBonus = inputs.bonusStats[stat]!;
  const traitModifier = traitDerivedModifier(stat, inputs.traits, inputs.baseStats, game);
  const readTimeModifier = perkDerivedModifier(stat, inputs.perks ?? [], game);
  const raw = storedBase + storedBonus + traitModifier + readTimeModifier;
  const bounds = forGame(game).statBounds[stat];
  const displayed = bounds ? Math.min(Math.max(raw, bounds[0]), bounds[1]) : raw;
  return {
    storedBase,
    storedBonus,
    traitModifier,
    readTimeModifier,
    raw,
    displayed,
    minimum: bounds?.[0] ?? null,
    maximum: bounds?.[1] ?? null,
  };
}

export function computeDerivedDisplay(stat: number, inputs: DerivedInputs): number {
  return computeDerivedDisplayBreakdown(stat, inputs).displayed;
}

/**
 * The GAIN_* perks each add one to their SPECIAL stat (`critterGetStat`).
 * Fallout 1 has none of them — its perk enum stops at `PERK_COMBAT_ARMOR` — so
 * its map is empty and the bonus is always zero.
 */
const GAIN_PERK_BY_STAT_BY_GAME: Record<SaveGameType, ReadonlyMap<number, number>> = {
  fo2: new Map(
    ['STRENGTH', 'PERCEPTION', 'ENDURANCE', 'CHARISMA', 'INTELLIGENCE', 'AGILITY', 'LUCK']
      .map((s) => [statIndex(s), perkIndex(`GAIN_${s}`)] as const)
      .filter(([, perk]) => perk >= 0),
  ),
  fo1: new Map(),
};

export type DerivedInputs = {
  baseStats: ArrayLike<number>;
  bonusStats: ArrayLike<number>;
  traits: ArrayLike<number>;
  /** The dude's perk row (the first `perkCount` entries of `save.perks`). */
  perks?: ArrayLike<number>;
  /**
   * Which game's trait, perk and stat-bound tables to read. Absent means
   * Fallout 2 — `derivedInputsFor` always sets it, and only a caller building
   * inputs by hand can leave it out.
   */
  game?: SaveGameType;
};

/**
 * `critterGetStat` for one of the SPECIAL seven: base, plus the trait modifier,
 * plus the stored bonus, plus the GAIN_* perk, clamped to the stat's bounds.
 *
 * Two engine effects are missing on purpose because they depend on state a save
 * file does not put where we can reach it: Adrenaline Rush (+1 Strength below
 * half hit points) and Mirrored Shades (+1 Charisma when worn) both need the
 * inventory, which this codec keeps opaque.
 */
export function computeSpecial(stat: number, inputs: DerivedInputs): number {
  const game = inputs.game ?? 'fo2';
  const gainPerk = GAIN_PERK_BY_STAT_BY_GAME[game].get(stat);
  const perkBonus = gainPerk !== undefined && (inputs.perks?.[gainPerk] ?? 0) > 0 ? 1 : 0;
  const raw = inputs.baseStats[stat]!
    + traitSpecialModifier(stat, inputs.traits, game)
    + inputs.bonusStats[stat]!
    + perkBonus;
  const [min, max] = forGame(game).statBounds[stat]!;
  return Math.min(Math.max(raw, min), max);
}

/**
 * Recompute the derived base stats exactly as `critterUpdateDerivedStats` does.
 *
 * Note which inputs each formula uses — the engine is not uniform about it.
 * Maximum hit points is built from `critterGetBaseStatWithTraitModifier`, so
 * base plus trait and *no* bonus; every other formula uses the full
 * `critterGetStat` value. Getting that backwards is a two-hit-point error that
 * only shows up on a character with a bonus to Strength or Endurance.
 */
export function computeDerivedBaseStats(inputs: DerivedInputs): Map<string, number> {
  // fallout1-ce `stat_recalc_derived` (stat.cc) is the same eleven formulas in
  // the same order as Fallout 2's `critterUpdateDerivedStats`, including the
  // max-HP asymmetry below, so this needs no fork.
  const game = inputs.game ?? 'fo2';
  const baseWithTrait = (name: string) => {
    const i = statIndex(name);
    return inputs.baseStats[i]! + traitSpecialModifier(i, inputs.traits, game);
  };
  const full = (name: string) => computeSpecial(statIndex(name), inputs);

  const per = full('PERCEPTION');
  const end = full('ENDURANCE');
  const agi = full('AGILITY');
  const luk = full('LUCK');
  const str = full('STRENGTH');

  return new Map([
    ['MAXIMUM_HIT_POINTS', baseWithTrait('STRENGTH') + baseWithTrait('ENDURANCE') * 2 + 15],
    ['MAXIMUM_ACTION_POINTS', Math.floor(agi / 2) + 5],
    ['ARMOR_CLASS', agi],
    ['MELEE_DAMAGE', Math.max(str - 5, 1)],
    ['CARRY_WEIGHT', 25 * str + 25],
    ['SEQUENCE', 2 * per],
    ['HEALING_RATE', Math.max(Math.floor(end / 3), 1)],
    ['CRITICAL_CHANCE', luk],
    ['BETTER_CRITICALS', 0],
    ['RADIATION_RESISTANCE', 2 * end],
    ['POISON_RESISTANCE', 5 * end],
  ]);
}

/** The derived inputs a parsed save carries. */
export function derivedInputsFor(save: SaveDat): DerivedInputs {
  return {
    baseStats: save.critter.baseStats,
    bonusStats: save.critter.bonusStats,
    traits: save.traits,
    perks: save.perks.subarray(0, K(save).perkCount),
    game: save.game,
  };
}

/** `traitGetSkillModifier` (engine/src/trait.cc). */
function traitSkillModifier(skill: number, traits: ArrayLike<number>, game: SaveGameType = 'fo2'): number {
  const names = forGame(game);
  const has = (name: string) => {
    const id = names.traitIndex(name);
    return id >= 0 && (traits[0] === id || traits[1] === id);
  };
  let modifier = has('GIFTED') ? -10 : 0;
  // fallout1-ce `trait_adjust_skill` (trait.cc) gives Skilled +10 to *every*
  // skill. Fallout 2's does not — there Skilled is five skill points a level
  // instead. Same trait name, different currency.
  if (game === 'fo1' && has('SKILLED')) modifier += 10;
  if (has('GOOD_NATURED')) {
    if (skill <= skillIndex('THROWING')) modifier -= 10;
    else if (['FIRST_AID', 'DOCTOR', 'SPEECH', 'BARTER'].some((s) => skillIndex(s) === skill)) modifier += 15;
  }
  return modifier;
}

/**
 * `perkGetSkillModifier` (engine/src/perk.cc). Ghost's +20 to Sneak is absent
 * because it is conditional on the dude's current light intensity, which is map
 * state rather than anything the save's stat block carries.
 */
function perkSkillModifier(skill: number, perks: ArrayLike<number>, game: SaveGameType = 'fo2'): number {
  const names = forGame(game);
  const has = (name: string) => {
    const id = names.perkIndex(name);
    return id >= 0 && (perks[id] ?? 0) > 0;
  };
  const is = (name: string) => skillIndex(name) === skill;
  if (game === 'fo1') {
    // fallout1-ce `perk_adjust_skill` (perk.cc). A shorter list than Fallout
    // 2's and the shared perks pay differently: Medic is +20 rather than +10,
    // Master Thief covers all four thief skills rather than adding on top of
    // Thief, and Mr Fixit and Speaker are +20. Ghost is omitted for the same
    // reason as in Fallout 2 — its +20 to Sneak is conditional on the dude's
    // current light level, which is map state a save's stat block does not
    // carry.
    let fo1 = 0;
    if (is('FIRST_AID') || is('DOCTOR')) {
      if (has('MEDIC')) fo1 += 20;
    } else if (is('SNEAK') || is('LOCKPICK') || is('STEAL') || is('TRAPS')) {
      if (has('MASTER_THIEF')) fo1 += 10;
    } else if (is('SCIENCE') || is('REPAIR')) {
      if (has('MR_FIXIT')) fo1 += 20;
    } else if (is('SPEECH') || is('BARTER')) {
      if (has('SPEAKER')) fo1 += 20;
    }
    return fo1;
  }
  let modifier = 0;
  if (is('FIRST_AID')) {
    if (has('MEDIC')) modifier += 10;
    if (has('VAULT_CITY_TRAINING')) modifier += 5;
  } else if (is('DOCTOR')) {
    if (has('MEDIC')) modifier += 10;
    if (has('LIVING_ANATOMY')) modifier += 10;
    if (has('VAULT_CITY_TRAINING')) modifier += 5;
  } else if (is('SNEAK') || is('LOCKPICK') || is('STEAL') || is('TRAPS')) {
    if (has('THIEF')) modifier += 10;
    if ((is('LOCKPICK') || is('STEAL')) && has('MASTER_THIEF')) modifier += 15;
    if (is('STEAL') && has('HARMLESS')) modifier += 20;
  } else if (is('SCIENCE') || is('REPAIR')) {
    if (has('MR_FIXIT')) modifier += 10;
  } else if (is('SPEECH') || is('BARTER')) {
    if (is('SPEECH')) {
      if (has('SPEAKER')) modifier += 20;
      if (has('EXPERT_EXCREMENT_EXPEDITOR')) modifier += 5;
    }
    if (has('NEGOTIATOR')) modifier += 10;
    if (is('BARTER') && has('SALESMAN')) modifier += 20;
  } else if (is('GAMBLING')) {
    if (has('GAMBLER')) modifier += 20;
  } else if (is('OUTDOORSMAN')) {
    if (has('RANGER')) modifier += 15;
    if (has('SURVIVALIST')) modifier += 25;
  }
  return modifier;
}

/**
 * `skillGetGameDifficultyModifier` (engine/src/skill.cc): +20 on Easy, −10 on
 * Hard, and only for the twelve non-combat skills. Zero on Normal.
 *
 * This used to be omitted with a note saying difficulty lived in the opaque part
 * of the file. It does not: it is the *first* word of group 17, which is a fixed
 * eighty bytes, and `preferencesLoad` pushes it into the settings the engine
 * then reads. So it is applied, and the panel says which difficulty it found.
 */
function difficultySkillModifier(skill: number, difficulty: 'easy' | 'normal' | 'hard' | null): number {
  const combat = ['SMALL_GUNS', 'BIG_GUNS', 'ENERGY_WEAPONS', 'UNARMED', 'MELEE_WEAPONS', 'THROWING'];
  if (combat.some((s) => skillIndex(s) === skill)) return 0;
  if (difficulty === 'easy') return 20;
  if (difficulty === 'hard') return -10;
  return 0;
}

/**
 * `skillGetValue` (engine/src/skill.cc), both numbers.
 *
 * The engine clamps to `skillMaximum` (300) as the very last thing it does,
 * after the tag doubling, the +20 tag bonus and every trait, perk and difficulty
 * modifier. That clamp is exactly the owner's complaint — 255 points and 280
 * points both read "300%" and the screen gives no hint that one of them is 40
 * points of nothing. So the uncapped sum is returned alongside the capped one
 * and the UI shows both.
 */
export function computeSkillValueDetailed(
  save: SaveDat,
  skill: number | string,
): SkillValueBreakdown {
  const constants = K(save);
  const game = save.game;
  let statsAveraged = false;
  const index = resolve('skill', skill, skillIndex, SKILL_COUNT);
  const description = constants.skillDescriptions[index]!;
  const inputs = derivedInputsFor(save);

  const rawPoints = save.critter.skills[index]!;
  const points = Math.max(rawPoints, 0);
  const pointsContribution = points * description.baseValueMult;
  const statContributions: SkillValueBreakdown['statContributions'] = [];
  let value = description.defaultValue + pointsContribution;
  if (constants.skillAveragesStats && description.stat2 !== -1) {
    // fallout1-ce `skill_level` (skill.cc:186): a two-stat skill uses
    // `(stat1 + stat2) * modifier / 2` — one integer division of the *sum*, not
    // two independent contributions. Fallout 2 adds each stat separately, which
    // is the branch below. Splitting the FO1 total across the two rows keeps the
    // breakdown panel honest about where it came from while the arithmetic stays
    // the engine's.
    const a = computeSpecial(description.stat1, inputs);
    const b = computeSpecial(description.stat2, inputs);
    const combined = Math.trunc((a + b) * description.statModifier / 2);
    statsAveraged = true;
    statContributions.push(
      { stat: description.stat1, statValue: a, multiplier: description.statModifier, contribution: combined },
      { stat: description.stat2, statValue: b, multiplier: description.statModifier, contribution: 0 },
    );
    value += combined;
  } else {
    for (let stat = 0; stat < PRIMARY_STAT_COUNT; stat++) {
      if (stat === description.stat1 || stat === description.stat2) {
        const statValue = computeSpecial(stat, inputs);
        const contribution = statValue * description.statModifier;
        statContributions.push({ stat, statValue, multiplier: description.statModifier, contribution });
        value += contribution;
      }
    }
  }

  const tagged = Array.from(save.taggedSkills).includes(index);
  let tagPointsContribution = 0;
  let tagFlatBonus = 0;
  if (tagged) {
    tagPointsContribution = pointsContribution;
    value += tagPointsContribution;
    // Fallout 2's Tag! perk suppresses the flat +20 on the fourth slot it grants
    // (`skillGetValue`). fallout1-ce's `skill_level` has no such exception: every
    // tagged skill gets `20 + points` regardless of how the slot was acquired.
    const tagPerk = constants.perkIndex('TAG');
    const suppressed = game === 'fo2'
      && tagPerk >= 0
      && (inputs.perks?.[tagPerk] ?? 0) > 0
      && index === save.taggedSkills[3];
    if (!suppressed) {
      tagFlatBonus = TAG_SKILL_BONUS;
      value += tagFlatBonus;
    }
  }
  const traitModifier = traitSkillModifier(index, save.traits, game);
  const perkModifier = perkSkillModifier(index, inputs.perks ?? [], game);
  const difficultyModifier = difficultySkillModifier(index, gameDifficulty(save));
  value += traitModifier;
  value += perkModifier;
  value += difficultyModifier;

  let negativePointsAdjustment = 0;
  // fallout1-ce `skill_level` returns the raw points immediately when they are
  // negative (skill.cc:174) — no formula runs at all. Fallout 2 instead folds a
  // floored negative contribution into the sum, which is the branch below. A
  // Fallout 1 save with negative stored points is not reachable through the game,
  // but expert mode can write one, so the engine's own answer is what is shown.
  if (rawPoints < 0 && game === 'fo1') {
    return {
      statsAveraged: false,
      value: rawPoints,
      uncapped: rawPoints,
      capped: false,
      startingValue: description.defaultValue,
      rawPoints,
      pointsMultiplier: description.baseValueMult,
      pointsContribution: 0,
      statContributions: [],
      tagged,
      tagPointsContribution: 0,
      tagFlatBonus: 0,
      traitModifier: 0,
      perkModifier: 0,
      difficultyModifier: 0,
      negativePointsAdjustment: 0,
    };
  }
  if (rawPoints < 0) {
    negativePointsAdjustment = Math.max(rawPoints, SKILLS_MIN_RAW_POINTS) * description.baseValueMult;
    if (tagged) negativePointsAdjustment *= 2;
    value += negativePointsAdjustment;
  }

  const maximum = constants.skillMaximum;
  let uncapped = Math.trunc(value);
  if (rawPoints < 0 && uncapped < 0) uncapped = Math.max(uncapped, SKILLS_MIN_VALUE);
  return {
    statsAveraged,
    value: Math.min(uncapped, maximum),
    uncapped,
    capped: uncapped > maximum,
    startingValue: description.defaultValue,
    rawPoints,
    pointsMultiplier: description.baseValueMult,
    pointsContribution,
    statContributions,
    tagged,
    tagPointsContribution,
    tagFlatBonus,
    traitModifier,
    perkModifier,
    difficultyModifier,
    negativePointsAdjustment,
  };
}

export type SkillValueBreakdown = {
  /**
   * True when the two stat rows are a single averaged term rather than two
   * independent contributions — Fallout 1's `skill_level`. The whole term is
   * carried on the first row and the second row's contribution is zero, so a
   * display that adds the rows up still gets the right total; this flag is what
   * lets it *say* the right thing.
   */
  statsAveraged: boolean;
  value: number;
  uncapped: number;
  capped: boolean;
  startingValue: number;
  rawPoints: number;
  pointsMultiplier: number;
  pointsContribution: number;
  statContributions: {
    stat: number;
    statValue: number;
    multiplier: number;
    contribution: number;
  }[];
  tagged: boolean;
  tagPointsContribution: number;
  tagFlatBonus: number;
  traitModifier: number;
  perkModifier: number;
  difficultyModifier: number;
  negativePointsAdjustment: number;
};

/** The percentage the character screen shows. `skillGetValue`, clamped. */
export function computeSkillValue(save: SaveDat, skill: number | string): number {
  return computeSkillValueDetailed(save, skill).value;
}

function requireInt(field: string, value: number): number {
  if (!Number.isInteger(value)) throw new EditError(field, value, 'must be a whole number');
  return value;
}

function requireRange(field: string, value: number, min: number, max: number): number {
  requireInt(field, value);
  if (value < min || value > max) {
    throw new EditError(field, value, `must be between ${min} and ${max}`);
  }
  return value;
}

function resolve(
  kind: string,
  key: number | string,
  lookup: (name: string) => number,
  count: number,
): number {
  const index = typeof key === 'number' ? key : lookup(key);
  if (index < 0 || index >= count) {
    throw new EditError(kind, key, `unknown ${kind} (expected 0..${count - 1} or a known name)`);
  }
  return index;
}

/**
 * A mutable editing session over a parsed save.
 *
 * Mutates the `SaveDat` in place; `serializeSaveDat` then re-encodes it. Each
 * setter validates against the engine's own bounds and records what it changed,
 * so a UI can show a change list and an explanation without re-deriving either.
 */
export class SaveEditor {
  readonly save: SaveDat;
  readonly changes: EditRecord[] = [];
  private readonly warnings: EditWarning[] = [];
  private specialTouched = false;

  constructor(save: SaveDat) {
    this.save = save;
  }

  /** Whichever game's tables this save is indexed by. See `K` above. */
  private get K(): GameConstants {
    return forGame(this.save.game);
  }

  /**
   * Apply a validated mutation and record it.
   *
   * The mutation runs through `apply` rather than beside it, so the recorded
   * closure is provably the change that was made — which is what makes
   * `replayInto` trustworthy. Only a change that moves the value is recorded;
   * the mutation itself always runs, so a "set it to what it already is" is a
   * no-op rather than a phantom row in the change list.
   */
  private change(
    what: string,
    from: number | string,
    to: number | string,
    apply: (save: SaveDat) => void,
    afterEngineWrite = false,
  ): void {
    apply(this.save);
    if (from !== to) this.changes.push({ what, from, to, afterEngineWrite, apply });
  }

  /**
   * Re-apply every recorded change to another parse of the same slot, in order.
   *
   * Used when the engine bridge has rewritten the slot underneath us: the shell
   * re-parses what the engine wrote and replays the scalar edits on top. Only
   * the fields the player actually touched move; everything the engine changed
   * for its own reasons is left exactly as the engine wrote it.
   */
  replayInto(save: SaveDat): void {
    for (const change of this.changes) change.apply(save);
  }

  /** Replay only shell-owned header fields after engine truth has been saved. */
  replayAfterEngineWriteInto(save: SaveDat): void {
    for (const change of this.changes) {
      if (change.afterEngineWrite) change.apply(save);
    }
  }

  private warn(kind: EditWarning['kind'], message: string): void {
    if (!this.warnings.some((w) => w.kind === kind && w.message === message)) {
      this.warnings.push({ kind, message });
    }
  }

  private setCritterFlag(flag: number, enabled: boolean, label: string): void {
    const before = this.save.critter.flags;
    const after = enabled ? before | flag : before & ~flag;
    this.change(label, before, after, (save) => {
      save.critter.flags = enabled ? save.critter.flags | flag : save.critter.flags & ~flag;
    });
  }

  /** Mutate the parsed queue while preserving every unrelated event. */
  private mutateQueue(what: string, mutate: (events: QueueEvent[], save: SaveDat) => void): void {
    // Fallout 2 needs `queueLayout` because its queue lives inside the opaque
    // tail and is addressed relative to it. Fallout 1's is a section of its own,
    // between the PC stats and the traits, so the parsed events are enough.
    const located = this.save.queueEvents !== null
      && (this.save.format.queueBeforeTraits || this.save.queueLayout !== null);
    if (!located) {
      throw new EditError(
        'event queue',
        what,
        'could not be located safely in this save; the condition cannot be changed without risking the opaque tail',
      );
    }
    const clone = (events: readonly QueueEvent[]) => events.map((event) => ({
      ...event,
      data: Int32Array.from(event.data),
    }));
    const signature = (events: readonly QueueEvent[]) => JSON.stringify(events.map((event) => [
      event.time,
      event.type,
      event.ownerId,
      ...event.data,
    ]));
    const current = this.save.queueEvents ?? [];
    const preview = clone(current);
    mutate(preview, this.save);
    preview.sort((a, b) => a.time - b.time);
    const from = signature(current);
    const to = signature(preview);
    this.change(what, from, to, (save) => {
      if (!save.queueEvents) throw new Error('event queue disappeared while replaying an edit');
      const next = clone(save.queueEvents);
      mutate(next, save);
      next.sort((a, b) => a.time - b.time);
      save.queueEvents = next;
      const dudeQueued = next.some((event) => event.ownerId === save.dude.id);
      save.dude.flags = dudeQueued ? save.dude.flags | OBJECT_QUEUED : save.dude.flags & ~OBJECT_QUEUED;
    });
  }

  /** Warnings accumulated by the edits applied so far. */
  get pendingWarnings(): readonly EditWarning[] {
    if (!this.specialTouched) return this.warnings;
    const stale: string[] = [];
    const derived = computeDerivedBaseStats(derivedInputsFor(this.save));
    for (const [name, expected] of derived) {
      const stored = this.save.critter.baseStats[statIndex(name)]!;
      if (stored !== expected) stale.push(`${name} is ${stored}, the formula gives ${expected}`);
    }
    if (stale.length > 0) {
      return [
        ...this.warnings,
        {
          kind: 'derived-stale' as const,
          message:
            'SPECIAL changed, but the engine does not recompute derived stats on load. '
            + `Until something in-game changes a SPECIAL stat (or the character screen runs), these stay as stored: ${stale.join('; ')}. `
            + 'Use recomputeDerivedStats() to apply the engine formulas now.',
        },
      ];
    }
    return this.warnings;
  }

  // --- SPECIAL and the rest of the stat block -----------------------------

  /**
   * Set a stored *base* stat. SPECIAL is stored pre-trait: the engine adds
   * Gifted and friends on read (`critterGetBaseStatWithTraitModifier`), so what
   * you write here is what the character sheet shows *minus* trait modifiers.
   */
  setStat(stat: number | string, value: number): this {
    const index = resolve('stat', stat, statIndex, SAVEABLE_STAT_COUNT);
    const name = STAT_NAMES[index]!;
    const [min, max] = this.K.statBounds[index]!;
    requireRange(`stat ${name}`, value, min, max);

    this.change(`base ${name}`, this.save.critter.baseStats[index]!, value, (save) => {
      save.critter.baseStats[index] = value;
    });

    if (index < PRIMARY_STAT_COUNT) {
      this.specialTouched = true;
    } else if (DERIVED_BASE_STATS.has(name)) {
      this.warn(
        'engine-would-refuse',
        `${name} is a derived stat: the engine's own critterSetBaseStat refuses to set it, `
        + `and critterUpdateDerivedStats will overwrite it (${DERIVED_BASE_STATS.get(name)}) `
        + 'the next time a SPECIAL stat changes in-game.',
      );
    }
    return this;
  }

  /** Set a stored *bonus* stat. `critterGetStat` returns base + bonus + modifiers. */
  setBonusStat(stat: number | string, value: number): this {
    const index = resolve('stat', stat, statIndex, SAVEABLE_STAT_COUNT);
    const name = STAT_NAMES[index]!;
    requireRange(`bonus ${name}`, value, -999, 999);
    this.change(`bonus ${name}`, this.save.critter.bonusStats[index]!, value, (save) => {
      save.critter.bonusStats[index] = value;
    });
    if (index < PRIMARY_STAT_COUNT) this.specialTouched = true;
    return this;
  }

  /** Apply `critterUpdateDerivedStats` to the stored base stats, now. */
  recomputeDerivedStats(): this {
    for (const [name, value] of computeDerivedBaseStats(derivedInputsFor(this.save))) {
      const index = statIndex(name);
      this.change(`base ${name} (recomputed)`, this.save.critter.baseStats[index]!, value, (save) => {
        save.critter.baseStats[index] = value;
      });
    }
    return this;
  }

  // --- engine mode --------------------------------------------------------
  //
  // The methods below are the ones engine mode uses. They differ from their
  // free-edit counterparts in one way only: they do everything the engine would
  // have done as a consequence, immediately, rather than writing one number and
  // leaving a warning behind. Expert mode keeps the plain setters.

  /**
   * Set a SPECIAL stat and run the recompute the engine runs.
   *
   * In the game, `critterSetBaseStat` ends with `critterUpdateDerivedStats`
   * (engine/src/stat.cc:588). There is no state in which a SPECIAL stat has
   * moved and the derived stats have not — so in engine mode there is no
   * stale-derived warning to dismiss, because the situation it warns about
   * cannot be reached.
   */
  setStatLinked(stat: number | string, value: number): this {
    const index = resolve('stat', stat, statIndex, SAVEABLE_STAT_COUNT);
    if (index >= PRIMARY_STAT_COUNT) {
      throw new EditError(
        'stat',
        STAT_NAMES[index] ?? index,
        'is a derived stat: the engine computes it and refuses to set it. Change the SPECIAL stat it '
          + 'is made from, or switch to expert mode to write it directly.',
      );
    }
    this.setStat(index, value);
    return this.recomputeDerivedStats();
  }

  /** Set the static character-screen value, converting it back to the pre-trait
   * base stored in SAVE.DAT. Conditional equipment/map modifiers are deliberately
   * resolved again by the engine when the queued operation is committed. */
  setSpecialDisplayLinked(stat: number | string, value: number): this {
    const index = resolve('stat', stat, statIndex, PRIMARY_STAT_COUNT);
    requireRange(`stat ${STAT_NAMES[index]}`, value, PRIMARY_STAT_MIN, PRIMARY_STAT_MAX);
    const current = computeSpecial(index, derivedInputsFor(this.save));
    const raw = this.save.critter.baseStats[index]! + value - current;
    requireRange(`stored base ${STAT_NAMES[index]}`, raw, PRIMARY_STAT_MIN, PRIMARY_STAT_MAX);
    this.setStat(index, raw);
    return this.recomputeDerivedStats();
  }

  /**
   * Choose traits the way the character editor does: set the slot, then run the
   * recompute, because a trait moves SPECIAL and SPECIAL feeds the derived
   * stats.
   */
  setTraitLinked(slot: number, trait: number | string | null): this {
    this.setTrait(slot, trait);
    return this.recomputeDerivedStats();
  }

  /**
   * Set the level, and everything the engine ties to it.
   *
   * Six things move together, in this order:
   *
   *   1. **Level.**
   *   2. **Experience** snaps to that level's minimum (`pcGetExperienceForLevel`).
   *      It is then editable upward inside the band by `setExperienceLinked`.
   *   3. **Unspent skill points** accrue one level at a time, with the engine's
   *      clamp applied at each step rather than at the end — banking past 99 is
   *      not reachable in the game and is not reachable here.
   *   4. **`gCharacterEditorLastLevel`** is moved to the new level, and the owed
   *      perk count to the number of slots the new level is worth.
   *   5. **Maximum and current HP** move by the engine's per-level amount.
   *   6. **The level-up-available critter flag** follows `pcSetExperience` and
   *      is cleared (Here and Now uses the separate XP-add path and restores it).
   *
   * Step 4 is the one that is easy to miss and impossible to skip. The character
   * screen runs a catch-up loop from the stored last level every time it opens
   * (`characterEditorUpdateLevel`); leaving that word behind means the game hands
   * out the same skill points a second time the moment the player presses the
   * character button. Moving it is what makes step 3 correct rather than double.
   */
  setLevelLinked(level: number): this {
    requireRange('level', level, 1, levelCap(this.save.game));
    const from = this.save.pcStats[pcStatIndexOf('LEVEL')]!;
    let hpLoss = 0;
    if (level < from) {
      const endurance = this.save.critter.baseStats[statIndex('ENDURANCE')]!
        + traitSpecialModifier(statIndex('ENDURANCE'), this.save.traits, this.save.game);
      const hpPerLevel = Math.floor(endurance / 2) + 2 + this.getPerkRank('LIFEGIVER') * 4;
      hpLoss = hpPerLevel * (from - level);
      if (this.save.dude.hp - hpLoss <= 0) {
        throw new EditError(
          'level',
          level,
          `would reduce current HP to ${this.save.dude.hp - hpLoss} and invoke the engine's death path; `
            + 'this editor cannot reconstruct all death-state side effects safely',
        );
      }
    }
    this.setPcStat('LEVEL', level);

    const floor = experienceForLevel(level, this.save.game);
    this.setPcStat('EXPERIENCE', floor);

    if (level > from) {
      const unspent = this.save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]!;
      const accrued = accrueSkillPoints(unspent, from, level, this.levelUpInputs(), this.save.game);
      this.setPcStat('UNSPENT_SKILL_POINTS', accrued);

      // pcAddExperienceWithOptions grants this on every crossed level, adding
      // it to both maximum and current HP after the level is raised.
      const endurance = this.save.critter.baseStats[statIndex('ENDURANCE')]!
        + traitSpecialModifier(statIndex('ENDURANCE'), this.save.traits, this.save.game);
      const hpPerLevel = Math.floor(endurance / 2) + 2 + this.getPerkRank('LIFEGIVER') * 4;
      const hpGain = hpPerLevel * (level - from);
      const maxHp = statIndex('MAXIMUM_HIT_POINTS');
      this.setBonusStat(maxHp, this.save.critter.bonusStats[maxHp]! + hpGain);
      const displayedMax = computeDerivedDisplay(maxHp, derivedInputsFor(this.save));
      this.setHitPoints(Math.min(this.save.dude.hp + hpGain, displayedMax));
    } else if (level < from) {
      // pcSetExperience applies the same current/max HP delta in reverse. It
      // does not refund skill points or already granted perks.
      this.setHitPoints(this.save.dude.hp - hpLoss);
      const maxHp = statIndex('MAXIMUM_HIT_POINTS');
      this.setBonusStat(maxHp, this.save.critter.bonusStats[maxHp]! - hpLoss);
    }

    // pcSetExperience clears this latch after either direction. Here and Now,
    // which uses pcAddExperienceWithOptions instead, restores it below.
    this.setCritterFlag(CRITTER_DUDE_LEVEL_UP_AVAILABLE, false, 'level-up available state');

    this.syncCharacterEditorState(from, level);
    return this;
  }

  /**
   * Set experience *within* the current level's band. Engine mode keeps level
   * and experience consistent, so an experience high enough to buy the next
   * level is a level change, not an experience change — and is refused here
   * with the level to use instead.
   */
  setExperienceLinked(experience: number): this {
    requireInt('experience', experience);
    if (experience < 0) throw new EditError('experience', experience, 'cannot be negative');
    const level = this.save.pcStats[pcStatIndexOf('LEVEL')]!;
    const band = experienceBand(level, this.save.game);
    if (experience < band.floor) {
      throw new EditError(
        'experience',
        experience,
        `is below level ${level}'s minimum of ${band.floor}. Lower the level first.`,
      );
    }
    if (band.ceiling !== null && experience > band.ceiling) {
      const wanted = levelForExperience(experience, this.save.game);
      throw new EditError(
        'experience',
        experience,
        `would make this character level ${wanted}. Set the level to ${wanted} instead — that moves the `
          + 'skill points and perk slots with it.',
      );
    }
    return this.setPcStat('EXPERIENCE', experience);
  }

  /**
   * Spend (or refund) unspent skill points to move a skill by `delta`
   * percentage points, at the engine's own cost curve.
   *
   * `skillAdd` (engine/src/skill.cc:553) charges `skillsGetCost(currentValue)`
   * per point and refuses when the displayed value is already at the cap, so
   * both of those happen here. The cost is re-read after every point, because
   * crossing 100% mid-purchase makes the next point cost two.
   *
   * Returns the editor; `skillSpendPlan` is the read-only version for a UI that
   * wants to show the price before the click.
   */
  spendSkillPoints(skill: number | string, delta: number): this {
    const index = resolve('skill', skill, skillIndex, SKILL_COUNT);
    requireInt(`skill ${SKILL_NAMES[index]}`, delta);
    if (delta === 0) return this;

    const plan = this.skillSpendPlan(index, delta);
    if (plan.refusal) throw new EditError(`skill ${SKILL_NAMES[index]}`, delta, plan.refusal);

    this.setSkillPoints(index, this.save.critter.skills[index]! + plan.points);
    this.setPcStat('UNSPENT_SKILL_POINTS', this.save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]! - plan.cost);
    return this;
  }

  /**
   * What `spendSkillPoints` would do, without doing it: how many raw points it
   * takes, what that costs from the unspent pool, and the reason it cannot be
   * done when it cannot.
   */
  skillSpendPlan(
    skill: number | string,
    delta: number,
  ): { points: number; cost: number; refusal: string | null } {
    const index = resolve('skill', skill, skillIndex, SKILL_COUNT);
    const unspent = this.save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]!;

    // Walking it is the only honest way: the cost of the next percent depends on
    // the value after the previous one, and the value depends on stats and tags
    // as well as points. A closed form would be right until it wasn't.
    const probe = { ...this.save, critter: { ...this.save.critter, skills: Int32Array.from(this.save.critter.skills) } } as SaveDat;
    let points = 0;
    let cost = 0;
    const step = delta > 0 ? 1 : -1;
    for (let i = 0; i < Math.abs(delta); i++) {
      const before = computeSkillValueDetailed(probe, index);
      const cap = skillDisplayCap(this.save.game);
      if (step > 0 && before.value >= cap) {
        return {
          points,
          cost,
          refusal:
            `is already at the engine's ${cap}% cap, and skillAdd refuses to raise a skill `
            + 'that has reached it. Further points buy nothing.',
        };
      }
      const price = skillPointCost(step > 0 ? before.value : before.value - 1, this.save.game);
      if (step > 0 && cost + price > unspent) {
        return {
          points,
          cost,
          refusal: `needs ${cost + price} skill points and only ${unspent} are unspent.`,
        };
      }
      // One raw point is one percent only below the cost bands; above them the
      // engine still adds one raw point and the *pool* pays more. Points and
      // cost are therefore tracked separately.
      probe.critter.skills[index] = probe.critter.skills[index]! + step;
      points += step;
      cost += step * price;
      if (step < 0 && probe.critter.skills[index]! < 0) {
        return { points, cost, refusal: 'cannot go below zero stored points. Expert mode can.' };
      }
    }
    return { points, cost, refusal: null };
  }

  /**
   * Take a perk the way the level-up dialog does: only if the character meets
   * the requirements, and only up to the perk's own maximum rank.
   *
   * The requirement list comes back either way, so a refusal can be shown as the
   * specific line that failed rather than a flat "no".
   */
  addPerkChecked(perk: number | string): PerkAvailability {
    const index = resolve('perk', perk, this.K.perkIndex, this.K.perkCount);
    const availability = perkAvailability(index, perkContextFor(this.save));
    if (!availability.available) return availability;
    const owedBefore = characterEditorPerksOwed(this.save) ?? 0;
    if (owedBefore <= 0) {
      throw new EditError('perk', this.K.perkNames[index]!, 'cannot be taken because the character has no owed perk pick');
    }
    if (index === this.K.perkIndex('TAG') || index === this.K.perkIndex('MUTATE')) {
      throw new EditError(
        'perk',
        this.K.perkNames[index]!,
        'requires a follow-up skill or trait choice that this picker does not yet collect; no partial perk was written',
      );
    }
    if (index === this.K.perkIndex('HERE_AND_NOW')) {
      const level = this.save.pcStats[pcStatIndexOf('LEVEL')]!;
      if (level >= levelCap(this.save.game)) {
        throw new EditError('perk', this.K.perkNames[index]!, 'has no next level to grant');
      }
      const oldXp = this.save.pcStats[pcStatIndexOf('EXPERIENCE')]!;
      const nextFloor = experienceForLevel(level + 1, this.save.game);
      const rawGrant = nextFloor - oldXp;
      const swiftRank = this.getPerkRank('SWIFT_LEARNER');
      const swiftBonus = Math.trunc(swiftRank * 5 * rawGrant / 100);
      this.setPerkRank(index, this.getPerkRank(index) + 1);
      this.setLevelLinked(level + 1);
      this.setCritterFlag(CRITTER_DUDE_LEVEL_UP_AVAILABLE, true, 'level-up available state');
      const band = experienceBand(level + 1, this.save.game);
      this.setExperienceLinked(Math.min(nextFloor + swiftBonus, band.ceiling ?? nextFloor + swiftBonus));
    } else {
      this.setPerkRankLinked(index, this.getPerkRank(index) + 1);
      // These two bonuses are character-editor follow-ups, not entries in
      // perkAddEffect, so the generic linked rank change cannot supply them.
      if (index === this.K.perkIndex('LIFEGIVER')) {
        const maxHp = statIndex('MAXIMUM_HIT_POINTS');
        this.setBonusStat(maxHp, this.save.critter.bonusStats[maxHp]! + 4);
        this.setHitPoints(this.save.dude.hp + 4);
      } else if (index === this.K.perkIndex('EDUCATED')) {
        const unspent = this.save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]!;
        // pcSetStat rejects the whole +2 operation if it would exceed 99.
        if (unspent + 2 <= 99) this.setPcStat('UNSPENT_SKILL_POINTS', unspent + 2);
      }
    }
    // A perk taken here consumes a slot the game was holding open, if it was
    // holding one. Going below zero would be a slot the game never owed.
    const owed = characterEditorPerksOwed(this.save) ?? 0;
    if (owed > 0) {
      this.change('perk picks owed', owed, owed - 1, (save) => {
        setCharacterEditorPerksOwed(save, owed - 1);
      });
    }
    return availability;
  }

  /** The Intelligence, perk and trait inputs the level-up formula reads. */
  private levelUpInputs(): Parameters<typeof skillPointsForOneLevel>[0] {
    const int = statIndex('INTELLIGENCE');
    return {
      // `critterGetBaseStatWithTraitModifier`: base plus traits, and nothing
      // else. Not the character-screen value. Same in both games: fallout1-ce's
      // `UpdateLevel` reads `stat_get_base`, which adds `trait_adjust_stat` for
      // the player (stat.cc `stat_get_base`), so a Gifted character's +1 INT
      // counts there too — before the separate −5 for Gifted.
      intelligence: this.save.critter.baseStats[int]! + traitSpecialModifier(int, this.save.traits, this.save.game),
      educatedRank: this.getPerkRank('EDUCATED'),
      skilled: Array.from(this.save.traits).includes(this.K.traitIndex('SKILLED')),
      gifted: Array.from(this.save.traits).includes(this.K.traitIndex('GIFTED')),
    };
  }

  /**
   * Move `gCharacterEditorLastLevel` and the owed-perk byte to match a level the
   * editor just set, so the character screen does not re-run its catch-up.
   *
   * The owed count is **incremented by the perk levels the change crossed**, not
   * recomputed from the level. That distinction is load-bearing and the fixtures
   * proved it: `characterEditorGrantPerkAtLevel` does `gCharacterEditorHasFreePerk
   * += 1` per crossed level and nothing anywhere recomputes it, so a character
   * who *declined* a perk sits at fewer perks than their level earned, with zero
   * owed — which is a legitimate state, and one of the ten saves this was
   * developed against is in it (level 8, two slots earned, one perk taken, none
   * owed). Deriving `earned − taken` would have silently handed that player back
   * a perk they had turned down.
   *
   * Two of the engine's own conditions come with it: the grant is skipped
   * entirely once `kMaxSelectablePerks` distinct perks are held, and the counter
   * saturates at `kMaxPerkOwed`.
   *
   * What is *not* reproduced, because a save file does not record it:
   * `settings.gameplay.perk_carryover`. Under `PERK_CARRY_OVER_MODE_OFF` the
   * grant *assigns* 1 rather than incrementing, discarding any backlog. The
   * accumulating behaviour is what is implemented here, and the assumption is
   * declared in `rules.UNESTABLISHED`.
   */
  private syncCharacterEditorState(fromLevel: number, toLevel: number): void {
    const lastLevel = characterEditorLastLevel(this.save);
    if (lastLevel !== null && lastLevel !== toLevel) {
      this.change('character-screen last level', lastLevel, toLevel, (save) => {
        setCharacterEditorLastLevel(save, toLevel);
      });
    }

    const skilled = Array.from(this.save.traits).includes(this.K.traitIndex('SKILLED'));
    const crossed =
      perkSlotsThroughLevel(toLevel, skilled) - perkSlotsThroughLevel(fromLevel, skilled);
    if (crossed <= 0) return;
    if (countPerksTaken(this.save) >= maxSelectablePerks(this.save.game)) return;

    const current = characterEditorPerksOwed(this.save);
    if (current === null) return;
    // Fallout 1's `UpdateLevel` assigns `free_perk = 1` rather than adding, so
    // crossing three perk levels there owes one pick, not three.
    const owed = perkGrantAssigns(this.save.game)
      ? 1
      : Math.min(current + crossed, maxPerksOwed(this.save.game));
    if (owed === current) return;
    this.change('perk picks owed', current, owed, (save) => {
      setCharacterEditorPerksOwed(save, owed);
    });
  }

  // --- Hit points, radiation, poison -------------------------------------

  /**
   * Current hit points, stored on the dude `Object` (`data.critter.hp`), not in
   * the stat block. Nothing recomputes it on load, so this one sticks.
   */
  setHitPoints(value: number): this {
    requireRange('hitPoints', value, 0, 2000);
    const max = computeDerivedDisplay(statIndex('MAXIMUM_HIT_POINTS'), derivedInputsFor(this.save));
    if (value > max) {
      this.warn(
        'out-of-band',
        `hit points (${value}) exceed stored maximum hit points (${max}); the interface bar will show it, `
        + 'but the next heal or damage clamp will pull it back down.',
      );
    }
    this.change('hit points', this.save.dude.hp, value, (save) => {
      save.dude.hp = value;
    });
    return this;
  }

  setHitPointsLinked(value: number): this {
    const max = computeDerivedDisplay(statIndex('MAXIMUM_HIT_POINTS'), derivedInputsFor(this.save));
    requireRange('hitPoints', value, 1, max);
    return this.setHitPoints(value);
  }

  setRadiation(value: number): this {
    requireRange('radiation', value, 0, 2000);
    this.change('radiation', this.save.dude.radiation, value, (save) => {
      save.dude.radiation = value;
    });
    return this;
  }

  setRadiationLinked(value: number): this {
    const before = this.save.dude.radiation;
    this.setRadiation(value);
    // critterAdjustRadiation sets this latch only when positive radiation is
    // received. scriptsCheckGameEvents consumes it at the next radiation check.
    if (value > before) this.setCritterFlag(CRITTER_DUDE_RADIATED, true, 'radiation-check state');
    return this;
  }

  setPoison(value: number): this {
    requireRange('poison', value, 0, 2000);
    this.change('poison', this.save.dude.poison, value, (save) => {
      save.dude.poison = value;
    });
    return this;
  }

  setPoisonLinked(value: number): this {
    if (this.save.queueEvents === null
      || (!this.save.format.queueBeforeTraits && this.save.queueLayout === null)) {
      throw new EditError('poison timer', value, 'cannot be updated because this save\'s event queue was not parsed');
    }
    this.setPoison(value);
    this.mutateQueue('poison timer', (events, save) => {
      // critterAdjustPoison clears the one global poison event before adding a
      // replacement. Poison only operates on gDude in this engine.
      for (let i = events.length - 1; i >= 0; i--) {
        if (events[i]!.type === EVENT_TYPE_POISON) events.splice(i, 1);
      }
      if (value > 0) {
        events.push({
          time: (save.header.gameTime + poisonTicksToNext(value)) >>> 0,
          type: EVENT_TYPE_POISON,
          ownerId: save.dude.id,
          data: new Int32Array(0),
        });
      }
    });
    return this;
  }

  /**
   * Establish or clear the complete addiction constellation: GVAR, ADDICT bit,
   * withdrawal perk/effect and queue event. `addicted` is reserved for the two
   * script-controlled stock categories that have no generic onset.
   */
  setAddiction(
    rule: AddictionRule,
    gvarIndex: number,
    state: 'addicted' | 'pending' | 'withdrawal' | 'clear',
    allAddictionGvars: readonly number[],
    installed?: { onsetMinutes?: number | null; perk?: number | null },
  ): this {
    requireRange('addiction gvar', gvarIndex, 0, this.save.gvars.length - 1);
    if (!this.save.queueEvents
      || (!this.save.format.queueBeforeTraits && !this.save.queueLayout)) {
      throw new EditError('event queue', rule.name, 'cannot safely edit addiction timing in this save');
    }

    const onset = installed?.onsetMinutes === undefined ? rule.onsetMinutes : installed.onsetMinutes;
    const perk = installed?.perk === undefined ? rule.perk : installed.perk;
    if (state === 'pending' && onset === null) {
      throw new EditError('addiction', rule.name, 'is script-controlled and has no generic withdrawal onset');
    }
    if (state === 'withdrawal' && perk === null) {
      throw new EditError('withdrawal', rule.name, 'has no generic withdrawal perk');
    }

    const relatedPids = new Set([rule.pid, ...rule.alternatePids]);
    const flowerChild = this.getPerkRank('FLOWER_CHILD') > 0;
    const chemReliant = Array.from(this.save.traits).includes(traitIndex('CHEM_RELIANT'));

    if (perk !== null && this.getPerkRank(perk) > 0 && state !== 'withdrawal') {
      this.setPerkRankLinked(perk, 0);
    }
    if (state === 'withdrawal' && perk !== null && this.getPerkRank(perk) === 0) {
      this.setPerkRankLinked(perk, 1);
    }

    this.setGvar(gvarIndex, state === 'clear' ? 0 : 1);
    this.mutateQueue(`${rule.name} withdrawal timer`, (events, save) => {
      for (let i = events.length - 1; i >= 0; i--) {
        const event = events[i]!;
        if (event.type === EVENT_TYPE_WITHDRAWAL && relatedPids.has(event.data[1]!)) {
          events.splice(i, 1);
        }
      }
      if (state === 'pending') {
        events.push({
          time: (save.header.gameTime + onset! * GAME_TIME_TICKS_PER_MINUTE) >>> 0,
          type: EVENT_TYPE_WITHDRAWAL,
          ownerId: save.dude.id,
          data: new Int32Array([1, rule.pid, perk ?? -1]),
        });
      } else if (state === 'withdrawal') {
        const duration = withdrawalDurationMinutes({ chemReliant, flowerChild });
        events.push({
          time: (save.header.gameTime + duration * GAME_TIME_TICKS_PER_MINUTE) >>> 0,
          type: EVENT_TYPE_WITHDRAWAL,
          ownerId: save.dude.id,
          data: new Int32Array([0, rule.pid, perk!]),
        });
      }
    });

    const anyAddiction = allAddictionGvars.some((index) => index >= 0 && this.save.gvars[index] !== 0);
    this.setCritterFlag(CRITTER_DUDE_ADDICTED, anyAddiction, 'ADDICT indicator state');
    return this;
  }

  // --- Skills -------------------------------------------------------------

  /**
   * Set a skill's raw *points*, which is what the file stores. The percentage
   * the player sees is `skillGetValue`: a per-skill default, plus points times a
   * per-skill multiplier, plus stat contributions, plus the tag bonus. Points
   * are not percent, and 100 points is not 100%.
   */
  setSkillPoints(skill: number | string, points: number): this {
    const index = resolve('skill', skill, skillIndex, SKILL_COUNT);
    requireRange(`skill ${SKILL_NAMES[index]}`, points, SKILLS_MIN_RAW_POINTS, 400);
    this.change(`skill points ${SKILL_NAMES[index]}`, this.save.critter.skills[index]!, points, (save) => {
      save.critter.skills[index] = points;
    });
    return this;
  }

  /** Put a skill in a tag slot. Slots are 0..3; slot 3 is the Tag! perk's slot. */
  tagSkill(slot: number, skill: number | string | null): this {
    requireRange('tag slot', slot, 0, NUM_TAGGED_SKILLS - 1);
    const value = skill === null ? -1 : resolve('skill', skill, skillIndex, SKILL_COUNT);
    if (value !== -1) {
      for (let i = 0; i < NUM_TAGGED_SKILLS; i++) {
        if (i !== slot && this.save.taggedSkills[i] === value) {
          throw new EditError(
            'tag slot',
            SKILL_NAMES[value]!,
            `already tagged in slot ${i}; the engine's skillIsTagged would double-count it`,
          );
        }
      }
    }
    this.change(`tag slot ${slot}`, this.save.taggedSkills[slot]!, value, (save) => {
      save.taggedSkills[slot] = value;
    });
    return this;
  }

  // --- Perks --------------------------------------------------------------

  /**
   * Set a perk's rank. `party` is the perk row: 0 is the dude, 1.. are the party
   * slots declared in `party.txt`, in that file's order.
   */
  setPerkRank(perk: number | string, rank: number, party = 0): this {
    const index = resolve('perk', perk, this.K.perkIndex, this.K.perkCount);
    requireRange('perk rank', rank, 0, MAX_PERK_RANK);
    requireRange('party slot', party, 0, this.save.partySlotCount - 1);
    const at = party * this.K.perkCount + index;
    this.change(
      `perk ${this.K.perkNames[index]}${party === 0 ? '' : ` (party slot ${party})`}`,
      this.save.perks[at]!,
      rank,
      (save) => {
        save.perks[at] = rank;
      },
    );
    return this;
  }

  /**
   * Change a dude perk rank through perkAddEffect/perkRemoveEffect semantics.
   * The raw rank setter is intentionally retained for Expert mode.
   */
  setPerkRankLinked(perk: number | string, rank: number): this {
    const index = resolve('perk', perk, this.K.perkIndex, this.K.perkCount);
    requireRange('perk rank', rank, 0, MAX_PERK_RANK);
    const current = this.getPerkRank(index);
    const delta = rank - current;
    if (delta === 0) return this;

    const row: PerkRow = this.K.perkRows[index]!;
    const maxRank = row[1];
    if (maxRank !== -1 && rank > maxRank) {
      throw new EditError('perk rank', rank, `${this.K.perkNames[index]} has a maximum rank of ${maxRank}`);
    }

    this.setPerkRank(index, rank);

    // perkDescription.stat/statModifier applies once for every rank.
    const directStat = row[3];
    const directModifier = row[4];
    if (directStat !== -1 && directModifier !== 0) {
      this.setBonusStat(directStat, this.save.critter.bonusStats[directStat]! + delta * directModifier);
    }

    // Script/item-only perks (maxRank == -1) also carry seven SPECIAL deltas.
    if (maxRank === -1) {
      for (let stat = 0; stat < PRIMARY_STAT_COUNT; stat++) {
        const modifier = row[10 + stat]!;
        if (modifier !== 0) {
          this.setBonusStat(stat, this.save.critter.bonusStats[stat]! + delta * modifier);
        }
      }
    }

    if ((directStat >= 0 && directStat < PRIMARY_STAT_COUNT)
      || (maxRank === -1 && row.slice(10).some((v) => v !== 0))) {
      this.recomputeDerivedStats();
    }
    return this;
  }

  /** Give a perk one more rank. */
  addPerk(perk: number | string, party = 0): this {
    const index = resolve('perk', perk, this.K.perkIndex, this.K.perkCount);
    const at = party * this.K.perkCount + index;
    return this.setPerkRank(index, (this.save.perks[at] ?? 0) + 1, party);
  }

  removePerk(perk: number | string, party = 0): this {
    return this.setPerkRank(perk, 0, party);
  }

  getPerkRank(perk: number | string, party = 0): number {
    const index = resolve('perk', perk, this.K.perkIndex, this.K.perkCount);
    return this.save.perks[party * this.K.perkCount + index] ?? 0;
  }

  // --- Traits -------------------------------------------------------------

  setTrait(slot: number, trait: number | string | null): this {
    requireRange('trait slot', slot, 0, TRAITS_MAX_SELECTED_COUNT - 1);
    const value = trait === null ? -1 : resolve('trait', trait, this.K.traitIndex, this.K.traitCount);
    if (value !== -1) {
      for (let i = 0; i < TRAITS_MAX_SELECTED_COUNT; i++) {
        if (i !== slot && this.save.traits[i] === value) {
          throw new EditError('trait slot', this.K.traitNames[value]!, `already selected in slot ${i}`);
        }
      }
      this.warn(
        'consistency',
        'Traits shift SPECIAL on read (traitGetStatModifier), and the stored base stats are '
        + 'pre-trait. Changing a trait therefore changes every SPECIAL-derived number without '
        + 'changing a single stored stat — check the character screen after loading.',
      );
    }
    this.change(`trait slot ${slot}`, this.save.traits[slot]!, value, (save) => {
      save.traits[slot] = value;
    });
    return this;
  }

  // --- PC stats -----------------------------------------------------------

  setPcStat(stat: number | string, value: number): this {
    const index = resolve(
      'pc stat',
      stat,
      (name) => PC_STAT_NAMES.indexOf(name.toUpperCase() as (typeof PC_STAT_NAMES)[number]),
      PC_STAT_NAMES.length,
    );
    const [min, max] = PC_STAT_BOUNDS[index]!;
    requireRange(`pc stat ${PC_STAT_NAMES[index]}`, value, min, max);
    this.change(PC_STAT_NAMES[index]!, this.save.pcStats[index]!, value, (save) => {
      save.pcStats[index] = value;
    });
    return this;
  }

  /**
   * Set experience. The engine does not re-derive level from XP on load, and
   * `characterEditorSave` stores the last level separately (in the opaque tail),
   * so raising XP alone leaves the character under-levelled until the next
   * level-up check runs in-game.
   */
  setExperience(value: number): this {
    this.warn(
      'consistency',
      'Experience and level are stored separately and the load path reconciles neither. '
      + 'Set both, or expect the level-up to arrive only when the next XP award lands.',
    );
    return this.setPcStat('EXPERIENCE', value);
  }

  addExperience(delta: number): this {
    requireInt('experience delta', delta);
    const current = this.save.pcStats[PC_STAT_NAMES.indexOf('EXPERIENCE')]!;
    return this.setExperience(Math.max(0, current + delta));
  }

  setLevel(value: number): this {
    requireRange('level', value, 1, levelCap(this.save.game));
    return this.setPcStat('LEVEL', value);
  }

  setUnspentSkillPoints(value: number): this {
    return this.setPcStat('UNSPENT_SKILL_POINTS', value);
  }

  setKarma(value: number): this {
    return this.setPcStat('KARMA', value);
  }

  setReputation(value: number): this {
    return this.setPcStat('REPUTATION', value);
  }

  // --- Kill counts --------------------------------------------------------

  setKillCount(killType: number | string, value: number): this {
    const index = resolve('kill type', killType, this.K.killTypeIndex, this.K.killTypeCount);
    requireRange('kill count', value, 0, 1_000_000);
    this.change(`kills ${killType}`, this.save.kills[index]!, value, (save) => {
      save.kills[index] = value;
    });
    return this;
  }

  // --- Game globals -------------------------------------------------------

  /**
   * Set a GVAR by index. Quest flags are state machines, not booleans: a value
   * no script writes can leave a quest unfinishable. The editor's job is to
   * write the number the caller asked for and to be honest that it cannot know
   * whether that number is reachable.
   */
  setGvar(index: number, value: number): this {
    requireRange('gvar index', index, 0, this.save.gvars.length - 1);
    requireInt('gvar value', value);
    if (value < -0x80000000 || value > 0x7fffffff) {
      throw new EditError('gvar value', value, 'must fit in a signed 32-bit integer');
    }
    this.change(`GVAR ${index}`, this.save.gvars[index]!, value, (save) => {
      save.gvars[index] = value;
      if (save.gvarsMirror) {
        // This file's two GVAR blocks disagreed on parse, so we kept both. The
        // loader reads the first and discards the second; keep them in step from
        // here on rather than leaving a stale shadow copy.
        save.gvarsMirror[index] = value;
      }
    });
    return this;
  }

  getGvar(index: number): number {
    requireRange('gvar index', index, 0, this.save.gvars.length - 1);
    return this.save.gvars[index]!;
  }

  // --- Identity -----------------------------------------------------------

  /** Rename the character. Both the header field and what the UI lists change. */
  setCharacterName(name: string): this {
    if (name.length === 0) throw new EditError('characterName', name, 'must not be empty');
    if (name.length >= CHARACTER_NAME_SIZE) {
      throw new EditError('characterName', name, `must be under ${CHARACTER_NAME_SIZE} characters`);
    }
    this.change('character name', this.save.header.characterName, name, (save) => {
      save.header.characterName = name;
      save.header.characterNameRaw = fixedField(name, CHARACTER_NAME_SIZE);
    }, true);
    return this;
  }

  /** Change the slot description — the line the load screen shows. */
  setDescription(description: string): this {
    if (description.length >= DESCRIPTION_SIZE) {
      throw new EditError('description', description, `must be under ${DESCRIPTION_SIZE} characters`);
    }
    this.change('description', this.save.header.description, description, (save) => {
      save.header.description = description;
      save.header.descriptionRaw = fixedField(description, DESCRIPTION_SIZE);
    }, true);
    return this;
  }
}

/** `PC_STAT_NAMES` index, by name. A thin alias so call sites read as English. */
function pcStatIndexOf(name: (typeof PC_STAT_NAMES)[number]): number {
  return PC_STAT_NAMES.indexOf(name);
}

/** How many *distinct* perks the dude holds — what `kMaxSelectablePerks` counts. */
export function countPerksTaken(save: SaveDat): number {
  let taken = 0;
  for (let i = 0; i < K(save).perkCount; i++) if ((save.perks[i] ?? 0) > 0) taken++;
  return taken;
}

/**
 * The character, in the terms `perkCanAddAtLevel` compares against: effective
 * SPECIAL, displayed skill percentages, game globals and current perk ranks.
 */
export function perkContextFor(save: SaveDat): PerkContext {
  const inputs = derivedInputsFor(save);
  return {
    level: perkSelectionLevel(save),
    special: (stat) => computeSpecial(stat, inputs),
    skill: (skill) => computeSkillValue(save, skill),
    gvar: (index) => save.gvars[index] ?? 0,
    rank: (perk) => save.perks[perk] ?? 0,
    perkRows: K(save).perkRows,
    // Not redundant with `perkRows`: it also picks which engine's *comparison*
    // rules apply to a negative threshold. See `PerkContext.game`.
    game: save.game,
  };
}

/** Reproduce the engine's legacy owed-perk-level migration. The persisted save
 * has only a count; on load the engine reconstructs which historical level the
 * next selection belongs to from current level and ranks already spent. */
export function perkSelectionLevel(save: SaveDat): number {
  const currentLevel = save.pcStats[pcStatIndexOf('LEVEL')]!;
  const owed = characterEditorPerksOwed(save) ?? 0;
  if (owed <= 1) return currentLevel;
  const cadence = levelsPerPerk(Array.from(save.traits).includes(K(save).traitIndex('SKILLED')));
  const previousThresholds = Math.floor((currentLevel - 1) / cadence);
  let spentRanks = 0;
  for (let perk = 0; perk < K(save).perkCount; perk++) spentRanks += save.perks[perk] ?? 0;
  if (spentRanks >= previousThresholds) return currentLevel;
  const selection = Math.max(3, (spentRanks + 1) * cadence);
  return selection > 0 && selection <= currentLevel ? selection : currentLevel;
}

/**
 * Everything engine mode needs to say about levelling, computed rather than
 * asserted: where this character sits in its XP band, what the next level costs,
 * what a level-up is worth in skill points, and whether the character screen is
 * about to hand out anything the editor did not.
 *
 * That last field is the one worth watching. A save edited by any other tool —
 * or by this one in expert mode — can have a level ahead of the character
 * screen's own counter, and the game will quietly grant the difference in skill
 * points the next time the player opens the character sheet. Engine mode keeps
 * them in step; the panel reports it either way.
 */
export type LevelPlan = {
  level: number;
  experience: number;
  band: { floor: number; ceiling: number | null };
  /** XP still to go before the next level, or null at the top. */
  toNextLevel: number | null;
  skillPointsPerLevel: number;
  /** The Intelligence the level-up formula uses: base plus traits. */
  levelUpIntelligence: number;
  levelsPerPerk: number;
  perkSlotsEarned: number;
  perksTaken: number;
  perksOwed: number | null;
  /** `gCharacterEditorLastLevel`, or null when the tail is too short. */
  lastLevel: number | null;
  /**
   * Skill points the character screen will grant on its next visit because its
   * counter is behind the level. Zero when they are in step.
   */
  pendingCatchUpSkillPoints: number;
  difficulty: 'easy' | 'normal' | 'hard' | null;
};

export function levelPlan(save: SaveDat): LevelPlan {
  const level = save.pcStats[pcStatIndexOf('LEVEL')]!;
  const experience = save.pcStats[pcStatIndexOf('EXPERIENCE')]!;
  const constants = K(save);
  const band = experienceBand(level, save.game);
  const int = statIndex('INTELLIGENCE');
  // Base + trait in both games: fallout1-ce's `stat_get_base` adds the
  // trait modifier for the player, just like FO2's
  // `critterGetBaseStatWithTraitModifier`.
  const levelUpIntelligence = save.critter.baseStats[int]! + traitSpecialModifier(int, save.traits, save.game);
  const skilled = Array.from(save.traits).includes(constants.traitIndex('SKILLED'));
  const educatedRank = constants.perkIndex('EDUCATED') >= 0
    ? (save.perks[constants.perkIndex('EDUCATED')] ?? 0)
    : 0;
  const perLevel = skillPointsForOneLevel({
    intelligence: levelUpIntelligence,
    educatedRank,
    skilled,
    gifted: Array.from(save.traits).includes(constants.traitIndex('GIFTED')),
  }, save.game);
  const lastLevel = characterEditorLastLevel(save);
  const behind = lastLevel === null ? 0 : Math.max(0, level - lastLevel);

  return {
    level,
    experience,
    band,
    toNextLevel: band.ceiling === null ? null : band.ceiling + 1 - experience,
    skillPointsPerLevel: perLevel,
    levelUpIntelligence,
    levelsPerPerk: levelsPerPerk(skilled),
    perkSlotsEarned: perkSlotsThroughLevel(level, skilled),
    perksTaken: countPerksTaken(save),
    perksOwed: characterEditorPerksOwed(save),
    lastLevel,
    // The engine clamps the pool at each step, so a big gap does not multiply
    // out — it saturates. Reported the same way it will happen.
    pendingCatchUpSkillPoints:
      behind === 0
        ? 0
        : accrueSkillPoints(
            save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]!,
            lastLevel!,
            level,
            {
              intelligence: levelUpIntelligence,
              educatedRank,
              skilled,
              gifted: Array.from(save.traits).includes(constants.traitIndex('GIFTED')),
            },
            save.game,
          ) - save.pcStats[pcStatIndexOf('UNSPENT_SKILL_POINTS')]!,
    difficulty: gameDifficulty(save),
  };
}

/** Which perks the level-up dialog would offer this character right now. */
export function offeredPerks(save: SaveDat): PerkAvailability[] {
  const constants = K(save);
  const ctx = perkContextFor(save);
  const out: PerkAvailability[] = [];
  for (let perk = 0; perk < constants.perkCount; perk++) {
    if (perkMaxRank(perk, constants.perkRows) === -1) continue;
    out.push(perkAvailability(perk, ctx));
  }
  return out;
}

/** A read-only summary, for the panel that opens before anything is edited. */
export type CharacterSummary = {
  name: string;
  description: string;
  level: number;
  experience: number;
  hitPoints: number;
  maximumHitPoints: number;
  radiation: number;
  poison: number;
  special: Record<string, number>;
  skillPoints: Record<string, number>;
  taggedSkills: string[];
  traits: string[];
  perks: Record<string, number>;
  killTotal: number;
  gvarCount: number;
  partySlotCount: number;
};

export function summarize(save: SaveDat): CharacterSummary {
  const inputs = derivedInputsFor(save);
  const special: Record<string, number> = {};
  for (let i = 0; i < PRIMARY_STAT_COUNT; i++) {
    // What the character screen shows, not what the file stores: traits, the
    // stored bonus and the GAIN_* perks all land before the player sees it.
    special[STAT_NAMES[i]!] = computeSpecial(i, inputs);
  }
  const skillPoints: Record<string, number> = {};
  for (let i = 0; i < SKILL_COUNT; i++) skillPoints[SKILL_NAMES[i]!] = save.critter.skills[i]!;
  const constants = K(save);
  const perks: Record<string, number> = {};
  for (let i = 0; i < constants.perkCount; i++) {
    const rank = save.perks[i]!;
    if (rank > 0) perks[constants.perkNames[i]!] = rank;
  }
  const maxHpIndex = statIndex('MAXIMUM_HIT_POINTS');

  return {
    name: save.header.characterName,
    description: save.header.description,
    level: save.pcStats[1]!,
    experience: save.pcStats[2]!,
    hitPoints: save.dude.hp,
    maximumHitPoints: computeDerivedDisplay(maxHpIndex, inputs),
    radiation: save.dude.radiation,
    poison: save.dude.poison,
    special,
    skillPoints,
    taggedSkills: Array.from(save.taggedSkills, (s) => (s >= 0 ? SKILL_NAMES[s]! : '-')),
    traits: Array.from(save.traits, (t) => (t >= 0 ? constants.traitNames[t]! : '-')),
    perks,
    killTotal: save.kills.reduce((a, b) => a + b, 0),
    gvarCount: save.gvars.length,
    partySlotCount: save.partySlotCount,
  };
}

export type AddictionState = {
  addicted: boolean;
  withdrawalActive: boolean;
  pendingEvent: QueueEvent | null;
  endEvent: QueueEvent | null;
};

export function addictionState(
  save: SaveDat,
  rule: AddictionRule,
  gvarIndex: number,
  installedPerk: number | null = rule.perk,
): AddictionState {
  const pids = new Set([rule.pid, ...rule.alternatePids]);
  const events = (save.queueEvents ?? []).filter(
    (event) => event.type === EVENT_TYPE_WITHDRAWAL && pids.has(event.data[1]!),
  );
  return {
    addicted: gvarIndex >= 0 && (save.gvars[gvarIndex] ?? 0) !== 0,
    withdrawalActive: installedPerk !== null && installedPerk >= 0 && (save.perks[installedPerk] ?? 0) > 0,
    pendingEvent: events.find((event) => event.data[0] === 1) ?? null,
    endEvent: events.find((event) => event.data[0] === 0) ?? null,
  };
}
